import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '@spartacus/asm/root';
import {
  CmsProductCarouselComponent,
  Product,
  ProductActions,
  ProductService,
  UserIdService,
  WindowRef,
} from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ComponentService } from 'src/app/service/component.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';

export interface ShimanoCmsProductCarouselComponent extends CmsProductCarouselComponent {
  itemCount?: number;
  name?: string;
}

@Component({
  selector: 'app-product-carousel',
  templateUrl: './product-carousel.component.html',
  styleUrls: ['./product-carousel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductCarouselComponent implements OnInit, OnDestroy {
  private componentData$: Observable<ShimanoCmsProductCarouselComponent> = this.componentData.data$.pipe(
    filter(Boolean)
  );
  normal = true;
  dealerPrices: DealerPrice[];
  carouselLocation: string;
  itemCount: number;
  recentlyViewedProducts: string;
  products: Product[] = [];
  products$: Observable<any>[];
  productCodes: string[] = [];
  unapprovedProductCodes: string[] = [];
  unapprovedProductsFlag$ = new BehaviorSubject<boolean>(false);
  loadProductFailSubscription$: Subscription;
  userCurrent$: Observable<string> = this.userIdService.getUserId();
  asmEnabled = this.winRef.localStorage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  asmUserId: string;
  currentDealerCookieValue: string;
  currentSalesRepCookieValue: string;

  /**
   * returns an Obervable string for the title.
   */
  title$: Observable<string> = this.componentData$.pipe(map((data) => data.title));

  /**
   * Obervable that holds an Array of Observables. This is done, so that
   * the component UI could consider to lazy load the UI components when they're
   * in the viewpoint.
   */

  activeLanguage$: Observable<string> = this.languageService.getActive();
  getCountry: string;
  currentUID: string;
  cookieSubscribption$: Subscription;
  recentlyViewCookie$: Subscription;
  recentlyViewedCookieKey: string;
  combinedCookie: any[];
  combinedCookiesToUnshift: number;

  constructor(
    protected componentData: CmsComponentData<any>,
    protected productService: ProductService,
    protected cookieService: CookieService,
    protected dealerPriceService: DealerPriceService,
    protected cdr: ChangeDetectorRef,
    private actionsSubject: ActionsSubject,
    private languageService: ShimanoLanguageService,
    private userIdService: UserIdService,
    private winRef: WindowRef,
    private componentService: ComponentService,
    private storageService: StorageService
  ) {
    this.setRecentlyViewedCookieID();
  }

  items$ = this.unapprovedProductsFlag$.pipe(
    mergeMap(() => this.componentData$),
    tap((data) => {
      this.carouselLocation = data.name;
      this.itemCount = data.itemCount;
      this.recentlyViewedProducts = this.cookieService.get(this.currentUID);
    }),
    map((data) => {
      return data.typeCode === 'ShimanoRecentlyViewedComponent' && typeof this.recentlyViewedProducts !== 'undefined'
        ? this.recentlyViewedProducts.trim().split(' ').splice(0, this.itemCount)
        : data.productCodes
        ? data.productCodes.trim().split(' ').splice(0, this.itemCount)
        : [];
    }),
    mergeMap((productCodes) => {
      this.productCodes = productCodes;
      return forkJoin([of(productCodes), productCodes.length ? this.getDealerPrices(productCodes) : EMPTY]);
    }),
    map(([codes, dealerPrices]) => {
      this.dealerPrices = dealerPrices.dealerPrices;
      return codes.map((code) => this.productService.get(code));
    }),
    switchMap((data) => combineLatest(data)),
    map((items) => {
      const products = items.filter((e) => e);
      const mappedProducts = products.reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [currentValue.code]: currentValue,
        }),
        {}
      );
      if (products.length === items.length) {
        return this.productCodes.map((code) => {
          let mappedProduct: any;
          // @ts-ignore
          mappedProduct = mappedProducts[code];
          return mappedProduct;
        });
      }
    })
  );

  ngOnInit() {
    this.currentUID = this.storageService.getSessionStorageItem('recentlyViewedCookieKey');

    this.loadProductFailSubscription$ = this.actionsSubject
      .pipe(ofType(ProductActions.LOAD_PRODUCT_FAIL))
      .pipe(
        distinctUntilChanged(),
        tap((loadProductFailed: any) => {
          if (this.carouselLocation === 'Recently Viewed') {
            const unapprovedProductCode = loadProductFailed.meta.entityId as string;
            if (!this.unapprovedProductCodes.includes(unapprovedProductCode)) {
              this.unapprovedProductCodes.push(unapprovedProductCode);
            }

            const uniqUnapprovedProductCodes = new Set(this.unapprovedProductCodes);
            const diff = [...new Set(this.productCodes.filter((x) => !uniqUnapprovedProductCodes.has(x)))];
            this.cookieService.put(this.currentUID, diff.join(' '));
            if (diff.length) {
              this.unapprovedProductsFlag$.next(true);
            }
          }
        })
      )
      .subscribe();
  }

  setRecentlyViewedCookieID() {
    this.asmUserId = this.storageService.getLocalStorageItem('asmUserId');
    const userID = this.componentService.recentlyViewedUserID$.getValue();
    if (userID) {
      const currentDealerCookie = this.cookieService.get('recentlyViewedProduct_' + userID);
      if (currentDealerCookie !== null && currentDealerCookie !== undefined) {
        this.currentDealerCookieValue = currentDealerCookie;
      }

      // check if there is cookie from current User of dealer account impersonated by SalesRep
      const currentSalesRepCookie = this.cookieService.get('recentlyViewedProduct_' + userID + '_' + this.asmUserId);
      if (currentSalesRepCookie !== null && currentSalesRepCookie !== undefined) {
        this.currentSalesRepCookieValue = currentSalesRepCookie;
      }

      // If ASM is enabled, append asmUserId otherwise, set the cookie key without asmUserId
      const baseKey = 'recentlyViewedProduct_' + userID;
      this.recentlyViewedCookieKey = this.asmEnabled ? baseKey + '_' + this.asmUserId : baseKey;

      if (this.asmEnabled) {
        let putCookie = false;
        // Check if both dealer and sales rep cookie values exist
        if (this.currentDealerCookieValue) {
          // If the sales rep cookie does not include the dealer cookie value, update it
          if (
            !this.currentSalesRepCookieValue ||
            !this.currentSalesRepCookieValue.includes(this.currentDealerCookieValue)
          ) {
            // Append the dealer cookie value to the sales rep cookie
            this.currentSalesRepCookieValue = this.currentSalesRepCookieValue
              ? this.currentSalesRepCookieValue + ' ' + this.currentDealerCookieValue
              : this.currentDealerCookieValue;
            putCookie = true; // Mark that the cookie needs to be updated
          }
        }
        // If the cookie update flag is true, update the cookie
        if (putCookie) {
          this.cookieService.put(this.recentlyViewedCookieKey, this.currentSalesRepCookieValue);
        }
      } else if (this.currentDealerCookieValue) {
        // If ASM is not enabled, update the cookie with the dealers cookie value if present
        this.cookieService.put(this.recentlyViewedCookieKey, this.currentDealerCookieValue);
      }
      // Store the recently viewed cookie key in session storage for later use
      this.storageService.setSessionStorageItem('recentlyViewedCookieKey', this.recentlyViewedCookieKey);
    }
  }

  getDealerPrices(productCodes: string[]): Observable<any> {
    return this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
  }

  ngOnDestroy() {
    if (this.loadProductFailSubscription$) {
      this.loadProductFailSubscription$.unsubscribe();
    }
    if (this.recentlyViewCookie$) {
      this.recentlyViewCookie$.unsubscribe();
    }
    if (this.cookieSubscribption$) {
      this.cookieSubscribption$.unsubscribe();
    }
  }
}
